import { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useWallets } from '@wallet-standard/react';

import { encodedAddressPrefix, padding } from '../configs/constant';
import {
  getPaymentUtxos,
  getAdminData,
  mintCount,
  mintRecommend,
  mintCancel,
  mintRegister,
  mintTotal,
} from './api';
import {
  getFeeRate,
  bytesToHex,
  buf2hex,
  hexToBytes,
  getMempoolUtxos,
  waitSomeSeconds,
  addressReceivedMoneyInThisTx,
  pushBTCpmt,
  isValidTaprootAddress,
  fetchImageAsHex,
} from './inscribe-util';
import { BitcoinNetworkType, signTransaction } from 'sats-connect';

const envAddress = process.env.REACT_APP_ADDRESS;

/**
 * The whole mint flow - fee polling, the price timer, the supply counts and
 * the inscription itself - lifted out of the /mint page so the landing page
 * can run the same mint without sending anyone to another route.
 *
 * What this returns drives <MintDialogs />; a page only has to decide when to
 * call handleMintForm().
 */
export default function useMintFlow() {
  const { wallets } = useWallets();

  const SatsConnectNamespace = 'sats-connect:';

  const isSatsConnectCompatibleWallet = (wallet) => {
    return SatsConnectNamespace in wallet.features;
  };

  const wallet = useSelector((state) => state.wallet);

  // The landing page runs this too, so a missing tapscript must not take the
  // whole page down; handleMint bails out on it.
  const { Address, Script, Signer, Tap, Tx } = window.tapscript || {};
  const feeRateTabs = ['Slow', 'Normal', 'Fast', 'Custom'];

  const [feeRateMode, setFeeRateMode] = useState('Normal');
  const [feerate, setFeerate] = useState(0);
  const [feeRates, setFeeRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(1);
  const [deployLimit, setDeployLimit] = useState(21000);
  const [deployCount, setDeployCount] = useState(0);
  const [address, setAddress] = useState('');
  const [limit, setLimit] = useState(10);
  const [customFee, setCustomFee] = useState(0);
  const [sliderValue, setSliderValue] = useState(2);
  const [show, setShow] = useState(false);
  const [inscriptionStatus, setInscriptionStatus] = useState(false);
  const [showMintForm, setShowMintForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0.7);
  const [paymentLimit, setPaymentLimit] = useState(0.7);
  const [deductPayment, setDeductPayment] = useState(0.1);
  const [timerText, setTimerText] = useState('00:00:00');
  const [referencedate, setReferencedate] = useState('2024-07-03');
  const [mintFlag, setMintFlag] = useState(true);
  const [referencetime, setReferencetime] = useState(0);

  useEffect(() => {
    let intervalId;

    const updateFees = async () => {
      try {
        if (feeRateMode == 'Custom') {
          if (customFee == '' || customFee == '.' || parseInt(customFee) <= 1) {
            setFeerate(1.1);
          } else {
            setFeerate(parseInt(customFee));
          }
        } else {
          let response = await getFeeRate();
          setFeeRates(response);
          setFeerate(response[feeRateMode]);
          if (customFee == 0) {
            setCustomFee(response['Fast'] + 1);
          }
        }
      } catch (e) {
        console.log(e);
      }
    };
    updateFees();
    intervalId = setInterval(updateFees, 10 * 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [feeRateMode, amount, customFee]);

  useEffect(() => {
    let intervalId;

    const updateTimer = async () => {
      try {
        const gmtMinusFourTime = moment
          .tz('Etc/GMT+4')
          .format('YYYY-MM-DD HH:mm:ss');

        const currentDate = new Date(gmtMinusFourTime);
        const referenceDate = new Date(
          moment.tz(referencedate, 'Etc/GMT+4').format('YYYY-MM-DD HH:mm:ss')
        );

        if (currentDate < referenceDate) setMintFlag(false);

        let amount = Math.floor((currentDate - referenceDate) / 1000);

        let day = Math.floor(amount / 60 / 60 / 24);
        amount = (day + 1) * 60 * 60 * 24 - amount;
        let seconds = amount % 60;
        let temp = Math.floor(amount / 60);
        let minutes = temp % 60;
        temp = Math.floor(temp / 60);
        let hours = temp % 24;

        setPaymentAmount(formatAmount(paymentLimit - deductPayment * day));
        setTimerText(
          formatTime(hours) +
            ':' +
            formatTime(minutes) +
            ':' +
            formatTime(seconds)
        );
      } catch (e) {
        console.log(e);
      }
    };
    updateTimer();
    intervalId = setInterval(updateTimer, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [referencedate, referencetime]);

  useEffect(() => {
    const updateCounts = async () => {
      try {
        await handleUpdateCounts();
      } catch (e) {
        console.log(e);
      }
    };
    updateCounts();
  }, []);

  useEffect(() => {
    let intervalId;
    const handleAdminData = async () => {
      try {
        let result = await getAdminData();
        setReferencedate(handleReferencedate(result.start));
        setReferencetime(parseInt(result.start.split('-')[2]));
      } catch (e) {
        console.log(e);
      }
    };

    handleAdminData();
    intervalId = setInterval(handleAdminData, 100000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let value =
      (sliderValue / 100) * (500 - feeRates['Normal']) + feeRates['Normal'];
    if (Math.floor(value) <= 2) setCustomFee(2);
    else setCustomFee(Math.floor(value));
  }, [sliderValue]);

  useEffect(() => {
    setAddress(wallet.nostrOrdinalsAddress);
  }, [wallet.nostrOrdinalsAddress]);

  const handleReferencedate = (start) => {
    let startMonth = start.split('-')[0];
    let startDate = start.split('-')[1];
    let startTime = start.split('-')[2];

    if (parseInt(startMonth) <= 9) startMonth = '0' + startMonth;
    if (parseInt(startDate) <= 9) startDate = '0' + startDate;
    if (parseInt(startTime) <= 9) startTime = '0' + startTime;
    return (
      '2024' + '-' + startMonth + '-' + startDate + ' ' + startTime + ':00:00'
    );
  };

  const formatTime = (dateStr) => {
    if (dateStr < 10) return '0' + dateStr;
    else return '' + dateStr;
  };

  const formatAmount = (amount) => {
    if (amount < 0) return 0;
    else return Math.round(amount * 10) / 10;
  };

  const handleUpdateCounts = async () => {
    let results = await mintTotal();
    setDeployCount(results.data);
  };

  const getPaymentAmount = (value) => {
    const gmtMinusFourTime = moment
      .tz('Etc/GMT+4')
      .format('YYYY-MM-DD HH:mm:ss');

    const currentDate = new Date(gmtMinusFourTime);
    const referenceDate = new Date(
      moment.tz(referencedate, 'Etc/GMT+4').format('YYYY-MM-DD HH:mm:ss')
    );

    let amount = Math.floor((currentDate - referenceDate) / 1000);
    let day = Math.floor(amount / 60 / 60 / 24);
    amount = (day + 1) * 60 * 60 * 24 - amount;
    let seconds = amount % 60;
    let temp = Math.floor(amount / 60);
    let minutes = temp % 60;
    temp = Math.floor(temp / 60);
    let hours = temp % 24;

    return formatAmount(paymentLimit - deductPayment * day) * Math.pow(10, 8);
  };

  const handleMintForm = async (value) => {
    setLoading(true);
    let adminData = await getAdminData();
    if (adminData.status == 0) {
      toast.error('Mint Paused!');
    } else {
      setShowMintForm(true);
    }
    setLoading(false);
  };

  const handleMint = async () => {
    setLoading(true);
    setShow(true);
    setInscriptionStatus(false);

    let adminData = await getAdminData();

    setReferencedate(handleReferencedate(adminData.start));

    let mintResult = await mintCount(wallet.nostrOrdinalsAddress);
    let totalMintCount = await mintTotal();

    setShowMintForm(false);

    if (wallet.nostrOrdinalsAddress == '') {
      toast.error('Please connect wallet first!');
      setLoading(false);
      setShow(false);
      return;
    }

    if (adminData.status == 0) {
      toast.error('Mint Paused!');
      setLoading(false);
      setShow(false);
      return;
    }

    if (amount != parseInt(amount)) {
      toast.error('Please insert valid amount!');
      setLoading(false);
      setShow(false);
      return;
    }

    if (amount > limit) {
      toast.error('Amount can not exceed 10!');
      setLoading(false);
      setShow(false);
      return;
    }

    if (adminData.count < mintResult.data + amount) {
      toast.error('Maximum Inscriptions Exceeded!');
      setLoading(false);
      setShow(false);
      return;
    }

    if (amount > deployLimit - totalMintCount.data) {
      toast.error('Amount can not exceed the maximum amount!');
      setLoading(false);
      setShow(false);
      return;
    }

    if (address == '') {
      toast.error('Please insert wallet address!');
      setLoading(false);
      setShow(false);
      return;
    }

    if (!isValidTaprootAddress(address)) {
      toast.error('Please insert valid taproot address!');
      setLoading(false);
      setShow(false);
      return;
    }

    let fundingAddress = '';

    fundingAddress = adminData.address;

    let isSuccess = true;

    let inscriptionFee = 0;
    let fundingAmount = getPaymentAmount(
      handleReferencedate(adminData.start),
      parseInt(adminData.start.split('-')[2])
    );

    if (!typeof window) return;
    if (!window.tapscript) return;

    let cryptoUtils = window.cryptoUtils;
    const KeyPair = cryptoUtils.KeyPair;

    let privkey = bytesToHex(cryptoUtils.Noble.utils.randomPrivateKey());

    let seckey = new KeyPair(privkey);
    let pubkey = seckey.pub.rawX;

    const ec = new TextEncoder();

    const init_script = [pubkey, 'OP_CHECKSIG'];

    const init_script_backup = ['0x' + buf2hex(pubkey.buffer), 'OP_CHECKSIG'];

    let init_leaf = await Tap.tree.getLeaf(Script.encode(init_script));
    let [init_tapkey, init_cblock] = await Tap.getPubKey(pubkey, {
      target: init_leaf,
    });

    const test_redeemtx = Tx.create({
      vin: [
        {
          txid: 'a99d1112bcb35845fd44e703ef2c611f0360dd2bb28927625dbc13eab58cd968',
          vout: 0,
          prevout: {
            value: 10000,
            scriptPubKey: ['OP_1', init_tapkey],
          },
        },
      ],
      vout: [
        {
          value: 8000,
          scriptPubKey: ['OP_1', init_tapkey],
        },
      ],
    });

    const test_sig = await Signer.taproot.sign(seckey.raw, test_redeemtx, 0, {
      extension: init_leaf,
    });
    test_redeemtx.vin[0].witness = [test_sig.hex, init_script, init_cblock];
    const isValid = await Signer.taproot.verify(test_redeemtx, 0, { pubkey });

    if (!isValid) {
      alert('Generated keys could not be validated. Please reload the app.');
      return;
    }

    let tempfeerate = parseFloat(feerate);
    if (isNaN(tempfeerate) || tempfeerate <= 1) tempfeerate = 1.1;

    if (wallet.domain == 'xverseWallet') tempfeerate = tempfeerate * 1.25;

    let recommendResult = await mintRecommend(
      amount,
      address,
      privkey,
      tempfeerate
    );

    let files = [];

    let mimetype = 'image/webp';
    let ids = [];

    for (let temp of recommendResult.data) {
      const hexText = await fetchImageAsHex(
        'https://eyes4384.com/images/' + temp.number + '.webp'
      );
      console.log('--------------', hexText);
      ids.push(temp.id);

      files.push({
        text: JSON.stringify(hexText),
        name: hexText,
        hex: hexText,
        mimetype: mimetype,
        sha256: '',
      });
      await waitSomeSeconds(1);
    }

    let inscriptions = [];
    let inscriptionAddressList = [];

    let recipientList = [];
    recipientList.push({
      address: fundingAddress,
      amountSats: BigInt(fundingAmount),
    });

    let totalInscriptionFee = 0;

    for (let i = 0; i < files.length; i++) {
      const hex = files[i].hex;
      const data = hexToBytes(hex);
      const mimetype = ec.encode(files[i].mimetype);

      const script = [
        pubkey,
        'OP_CHECKSIG',
        'OP_0',
        'OP_IF',
        ec.encode('ord'),
        '01',
        mimetype,
        'OP_0',
        data,
        'OP_ENDIF',
      ];

      const script_backup = [
        '0x' + buf2hex(pubkey.buffer),
        'OP_CHECKSIG',
        'OP_0',
        'OP_IF',
        '0x' + buf2hex(ec.encode('ord')),
        '01',
        '0x' + buf2hex(mimetype),
        'OP_0',
        '0x' + buf2hex(data),
        'OP_ENDIF',
      ];

      const leaf = await Tap.tree.getLeaf(Script.encode(script));
      const [tapkey, cblock] = await Tap.getPubKey(pubkey, { target: leaf });

      let inscriptionAddress = Address.p2tr.encode(tapkey, encodedAddressPrefix);

      let prefix = 160;

      let txsize = prefix + Math.floor(data.length / 4);

      inscriptionAddressList.push(inscriptionAddress);

      inscriptionFee = getInscriptionFee(data.length);

      totalInscriptionFee += inscriptionFee;

      inscriptions.push({
        leaf: leaf,
        tapkey: tapkey,
        cblock: cblock,
        inscriptionAddress: inscriptionAddress,
        txsize: txsize,
        fee: inscriptionFee - padding,
        script: script_backup,
        script_orig: script,
      });

      recipientList.push({
        address: inscriptionAddress,
        amountSats: BigInt(inscriptionFee),
      });
    }

    let _fundingAddress = Address.p2tr.encode(init_tapkey, encodedAddressPrefix);

    let addressList = [];
    let totalFee =
      calculateFee(1, amount + 1, tempfeerate) + 50 + totalInscriptionFee;

    addressList.push(_fundingAddress);

    const paymentUtxos = await getPaymentUtxos(
      wallet.nostrPaymentAddress,
      addressList,
      totalFee,
      fundingAddress,
      fundingAmount,
      wallet.paymentPublicKey,
      tempfeerate,
      1,
      wallet.domain,
      amount
    );

    if (paymentUtxos.status == 'fail') {
      alert('Insufficient balance.');
      setLoading(false);
      setShow(false);
      isSuccess = false;
      await mintCancel(ids);
      return;
    }

    try {
      if (wallet.domain == 'tapwallet') {
        const signedPsbt = await window.tapwallet.signPsbt(paymentUtxos.psbt);
        const txid = await window.tapwallet.pushPsbt(signedPsbt);
      }
      if (wallet.domain == 'unisat') {
        const signedPsbt = await window.unisat.signPsbt(paymentUtxos.psbt);
        const txid = await window.unisat.pushPsbt(signedPsbt);
      }
      if (wallet.domain == 'okxwallet') {
        const signedPsbt = await window.okxwallet.bitcoin.signPsbt(
          paymentUtxos.psbt
        );
        const txid = await window.okxwallet.bitcoin.pushPsbt(signedPsbt);
      }
      if (wallet.domain == 'xverseWallet') {
        let res = paymentUtxos;
        if (res.status == 'success') {
          let signIndexes = [];
          for (let i = 0; i < res.count; i++) {
            signIndexes.push(i);
          }
          await signTransaction({
            payload: {
              network: {
                type: BitcoinNetworkType.Mainnet,
              },
              psbtBase64: res.psbt,
              broadcast: true,
              message:
                "tip the author! Don't worry this will not be broadcasted.",
              inputsToSign: [
                {
                  address: wallet.nostrPaymentAddress,
                  signingIndexes: signIndexes,
                },
              ],
            },
            onFinish: async (response) => {
              console.log('response: ', response);
            },
            onCancel: async () => {
              alert('Request canceled');
              setLoading(false);
              isSuccess = false;
              await mintCancel(ids);
              setShow(false);
            },
          });
        } else {
          alert('Insufficient balance.');
          setLoading(false);
          setShow(false);
          isSuccess = false;
          await mintCancel(ids);
          return;
        }
      }
      if (wallet.domain == 'magiceden') {
        let res = paymentUtxos;
        if (res.status == 'success') {
          let signIndexes = [];
          for (let i = 0; i < res.count; i++) {
            signIndexes.push(i);
          }

          let magicedenWallets = wallets.filter(isSatsConnectCompatibleWallet);

          await signTransaction({
            getProvider: async () =>
              magicedenWallets[0].features['sats-connect:'].provider,
            payload: {
              network: {
                type: BitcoinNetworkType.Mainnet,
              },
              psbtBase64: res.psbt,
              broadcast: true,
              message:
                "tip the author! Don't worry this will not be broadcasted.",
              inputsToSign: [
                {
                  address: wallet.nostrPaymentAddress,
                  signingIndexes: signIndexes,
                },
              ],
            },
            onFinish: async (response) => {},
            onCancel: async () => {
              alert('Request canceled');
              setLoading(false);
              isSuccess = false;
              setShow(false);
              await mintCancel(ids);
            },
          });
        } else {
          alert('Insufficient balance.');
          setLoading(false);
          setShow(false);
          isSuccess = false;
          await mintCancel(ids);
          return;
        }
      }
    } catch (e) {
      console.log(e);
      alert('Payment rejected by user. Try again.');
      isSuccess = false;
      setLoading(false);
      setShow(false);
      await mintCancel(ids);
      return;
    }

    if (isSuccess) {
      let transactionData;
      while (true) {
        transactionData = await getMempoolUtxos(_fundingAddress);
        if (transactionData.length >= 1) {
          break;
        }
        await waitSomeSeconds(2);
      }

      let txinfo = await addressReceivedMoneyInThisTx(_fundingAddress);

      let txid = txinfo[0];
      let vout = txinfo[1];
      let amt = txinfo[2];

      let outputs = [];

      let transaction = [];
      transaction.push({
        txsize: 60,
        vout: vout,
        script: init_script_backup,
        output: { value: amt, scriptPubKey: ['OP_1', init_tapkey] },
      });

      for (let i = 0; i < inscriptions.length; i++) {
        outputs.push({
          value: padding + inscriptions[i].fee,
          scriptPubKey: ['OP_1', inscriptions[i].tapkey],
        });

        transaction.push({
          txsize: inscriptions[i].txsize,
          vout: i,
          script: inscriptions[i].script,
          output: outputs[outputs.length - 1],
        });
      }

      if (
        paymentUtxos.address != undefined &&
        paymentUtxos.address.length > 10
      ) {
        outputs.push({
          value: paymentUtxos.value,
          scriptPubKey: [
            'OP_1',
            Address.p2tr.decode(paymentUtxos.address, encodedAddressPrefix).hex,
          ],
        });
      } else {
        outputs.push({
          value: paymentUtxos.value,
          scriptPubKey: [
            'OP_1',
            Address.p2tr.decode(envAddress, encodedAddressPrefix).hex,
          ],
        });
      }

      const init_redeemtx = Tx.create({
        vin: [
          {
            txid: txid,
            vout: vout,
            prevout: {
              value: amt,
              scriptPubKey: ['OP_1', init_tapkey],
            },
          },
        ],
        vout: outputs,
      });

      const init_sig = await Signer.taproot.sign(seckey.raw, init_redeemtx, 0, {
        extension: init_leaf,
      });
      init_redeemtx.vin[0].witness = [init_sig.hex, init_script, init_cblock];

      let rawtx = Tx.encode(init_redeemtx).hex;
      let _txid = await pushBTCpmt(rawtx);

      while (true) {
        transactionData = await getMempoolUtxos(
          inscriptions[0].inscriptionAddress
        );
        if (transactionData.length >= 1) {
          break;
        }
        await waitSomeSeconds(3);
      }

      for (let i = 0; i < inscriptions.length; i++) {
        await inscribe(
          inscriptions[i],
          i,
          transactionData[0].txid,
          inscriptions[i].fee + padding,
          seckey,
          ids[i],
          privkey
        );
      }
    } else {
      await mintCancel(ids);
    }

    await handleUpdateCounts();

    setLoading(false);
    setInscriptionStatus(true);
  };

  const inscribe = async (
    inscription,
    vout,
    txid2,
    amt2,
    seckey,
    registerId,
    privkey
  ) => {
    let _toAddress;
    let _script;
    let toAddress = address;
    if (toAddress.startsWith('tb1q') || toAddress.startsWith('bc1q')) {
      _toAddress = Address.p2wpkh.decode(toAddress, encodedAddressPrefix).hex;
      _script = ['OP_0', _toAddress];
      console.log('using p2wpkh', _script);
    } else if (
      toAddress.startsWith('1') ||
      toAddress.startsWith('m') ||
      toAddress.startsWith('n')
    ) {
      _toAddress = Address.p2pkh.decode(toAddress, encodedAddressPrefix).hex;
      _script = Address.p2pkh.scriptPubKey(_toAddress);
      console.log('using p2pkh', _script);
    } else if (toAddress.startsWith('3') || toAddress.startsWith('2')) {
      _toAddress = Address.p2sh.decode(toAddress, encodedAddressPrefix).hex;
      _script = Address.p2sh.scriptPubKey(_toAddress);
      console.log('using p2sh', _script);
    } else {
      _toAddress = Address.p2tr.decode(toAddress, encodedAddressPrefix).hex;
      _script = ['OP_1', _toAddress];
      console.log('using p2tr', _script);
    }

    const redeemtx = Tx.create({
      vin: [
        {
          txid: txid2,
          vout: vout,
          prevout: {
            value: amt2,
            scriptPubKey: ['OP_1', inscription.tapkey],
          },
        },
      ],
      vout: [
        {
          value: padding,
          scriptPubKey: _script,
        },
      ],
    });

    const sig = await Signer.taproot.sign(seckey.raw, redeemtx, 0, {
      extension: inscription.leaf,
    });
    redeemtx.vin[0].witness = [
      sig.hex,
      inscription.script_orig,
      inscription.cblock,
    ];

    console.dir(redeemtx, { depth: null });

    let rawtx2 = Tx.encode(redeemtx).hex;
    let _txid2;

    _txid2 = await pushBTCpmt(rawtx2);
    await sleep(1000);

    if (_txid2.includes('descendant')) {
      return;
    }

    try {
      JSON.parse(_txid2);
    } catch (e) {
      console.log(_txid2);
      /// Save inscription data
      ///
      if (_txid2.toString().length == 64) {
        let body = {
          id: registerId,
          number: registerId - 1,
          address: address,
          inscriptionId: _txid2 + 'i0',
          privateKey: privkey,
        };
        await mintRegister(body);
      }
    }
  };

  const sleep = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const addAmount = () => {
    let amountInt = parseInt(amount);
    setAmount(amountInt + 1);
  };

  const deductAmount = () => {
    let amountInt = parseInt(amount);
    if (amountInt > 1) setAmount(amountInt - 1);
  };

  const getInscriptionFee = (length) => {
    let prefix = 160;
    let txsize = prefix + Math.floor(length / 4);

    if (isNaN(parseFloat(feerate)) || feerate < 1) {
      Math.floor(txsize * 1.02) + padding;
    }
    return Math.floor(feerate * txsize * 1.02) + padding;
  };

  const handleCustomFee = (value) => {
    // Allow only integer values by removing non-digit characters
    const sanitizedValue = value.replace(/\D/g, '');
    setCustomFee(sanitizedValue);
  };

  const calculateFee = (
    vins,
    vouts,
    recommendedFeeRate,
    includeChangeOutput = 0
  ) => {
    if (typeof recommendedFeeRate !== 'number' || recommendedFeeRate <= 0)
      throw new Error('Invalid fee rate.');

    const baseTxSize = 10;
    const inSize = 57.5;
    const outSize = 43;
    const txSize =
      baseTxSize +
      vins * inSize +
      vouts * outSize +
      includeChangeOutput * outSize;
    const fee = Math.round(txSize * recommendedFeeRate);
    return fee;
  };

  return {
    feeRateTabs,
    feeRateMode,
    setFeeRateMode,
    feeRates,
    customFee,
    handleCustomFee,
    sliderValue,
    setSliderValue,
    amount,
    setAmount,
    addAmount,
    deductAmount,
    limit,
    address,
    setAddress,
    deployCount,
    deployLimit,
    paymentAmount,
    timerText,
    mintFlag,
    loading,
    show,
    setShow,
    inscriptionStatus,
    setInscriptionStatus,
    showMintForm,
    setShowMintForm,
    handleMintForm,
    handleMint,
  };
}
